(() => {
  "use strict";

  const root = () => {
    const r = window.Shopify?.routes?.root || "/";
    return r.endsWith("/") ? r : `${r}/`;
  };

  const UPDATE_URL = () => `${root()}cart/update.js`;
  const CART_URL = () => `${root()}cart.js`;
  const ADD_URL = () => `${root()}cart/add.js`;
  const CHANGE_URL = () => `${root()}cart/change.js`;

  const i18n = () => window.hrcI18n || {};
  const t = (key, fallback, vars = {}) => {
    let out = i18n()[key] || fallback;
    for (const [k, v] of Object.entries(vars)) {
      const val = String(v);
      out = out
        .replaceAll(`__${k}__`, val)
        .replaceAll(`__${k.toUpperCase()}__`, val)
        .replaceAll(`{{ ${k} }}`, val)
        .replaceAll(`{{${k}}}`, val);
    }
    if (vars.code != null) {
      out = out.replaceAll("__CODE__", String(vars.code)).replaceAll("{{ code }}", String(vars.code));
    }
    if (vars.amount != null) {
      out = out.replaceAll("__AMOUNT__", String(vars.amount)).replaceAll("{{ amount }}", String(vars.amount));
    }
    return out
      .replaceAll("&quot;", '"')
      .replaceAll("&#34;", '"')
      .replaceAll("&#39;", "'")
      .replaceAll("&apos;", "'")
      .replaceAll("&amp;", "&");
  };


  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function normalizeCode(value) {
    return String(value || "")
      .trim()
      .toUpperCase()
      .replace(/\s+/g, "");
  }

  function codesEqual(a, b) {
    return normalizeCode(a) === normalizeCode(b);
  }

  function formatMoney(cents) {
    if (window.HrcFormatMoney) {
      return window.HrcFormatMoney(cents, { forceDecimals: true });
    }
    const abs = Math.round(Math.abs(Number(cents) || 0));
    const grouped = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return `${grouped},${String(abs % 100).padStart(2, "0")}TL`;
  }

  async function fetchCart() {
    const res = await fetch(CART_URL(), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    });
    if (!res.ok) throw new Error(t("cartFetchFailed", "Could not load cart information"));
    return res.json();
  }

  async function updateDiscount(discountValue) {
    const res = await fetch(UPDATE_URL(), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ discount: discountValue }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok) {
      const message =
        data.description ||
        data.message ||
        data.errors ||
        t("discountApplyFailed", "Coupon could not be applied. Please check the code.");
      throw new Error(
        typeof message === "string" ? message : t("discountApplyFailedShort", "Coupon could not be applied."),
      );
    }

    return data;
  }

  async function addVariantToCart(variantId, quantity) {
    if (
      typeof window.HrcCartGuard?.isZeroPriceVariant === "function" &&
      window.HrcCartGuard.isZeroPriceVariant(variantId)
    ) {
      throw new Error(t("comingSoonCannotAdd", "This product is coming soon and cannot be added to cart."));
    }

    if (
      window.HrcGift?.isGiftOnlyProduct?.({
        variant_id: variantId,
        id: variantId,
      })
    ) {
      throw new Error(
        t("giftOnlyCannotAdd", "This product can only be given as a campaign gift."),
      );
    }

    const res = await fetch(ADD_URL(), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: Math.max(1, quantity) }],
      }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(
        data.description ||
          data.message ||
          t("discountAddFailedStock", "Could not add product to cart. Please check stock availability"),
      );
    }

    const listPrice = Number(
      data?.original_price != null ? data.original_price : data?.price,
    );
    if (
      Number.isFinite(listPrice) &&
      listPrice === 0 &&
      !window.HrcCartGuard?.isCampaignGiftPayload?.(data)
    ) {
      try {
        if (data?.key) {
          await fetch(`${root()}cart/change.js`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ id: data.key, quantity: 0 }),
          });
        }
      } catch (_) {
        /* ignore */
      }
      throw new Error(t("comingSoonCannotAdd", "This product is coming soon and cannot be added to cart."));
    }

    if (window.HrcCartGuard?.isUnauthorizedGiftItem?.(data) || window.HrcGift?.isUnauthorizedGift?.(data)) {
      try {
        if (data?.key) {
          await fetch(`${root()}cart/change.js`, {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({ id: data.key, quantity: 0 }),
          });
        }
      } catch (_) {
        /* ignore */
      }
      throw new Error(
        t("giftOnlyCannotAdd", "This product can only be given as a campaign gift."),
      );
    }

    return data;
  }

  async function changeLineQuantity(key, quantity) {
    if (!key) return null;
    const res = await fetch(CHANGE_URL(), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ id: key, quantity: Math.max(0, Number(quantity) || 0) }),
    });
    return res.json().catch(() => ({}));
  }

  function getAppliedCodes(cart) {
    return (cart?.discount_codes || [])
      .filter((d) => d.code && d.applicable === true)
      .map((d) => ({
        code: d.code,
        applicable: true,
      }));
  }

  function getActiveCodes(cart) {
    return getAppliedCodes(cart)
      .filter((d) => d.applicable)
      .map((d) => d.code);
  }

  function itemHasCouponDiscount(item) {
    if (!item) return false;
    if ((item.discounts || []).length > 0) return true;
    const original = Number(item.original_line_price || 0);
    const final = Number(item.final_line_price || item.line_price || 0);
    return original > 0 && final === 0;
  }

  function getVariantCouponedItems(cart, variantId) {
    const id = Number(variantId);
    if (!id) return [];
    return (cart?.items || []).filter(
      (item) =>
        Number(item.variant_id || item.id) === id && itemHasCouponDiscount(item),
    );
  }

  function getCouponTitleFromItem(item) {
    const title = item?.discounts?.[0]?.title || item?.discounts?.[0]?.code;
    return title ? String(title) : "";
  }

  /**
   * One active coupon at a time. Fully discounted (0 TL) products
   * cannot receive another coupon until the current one is removed.
   */
  function isCampaignSystemCode(code) {
    try {
      return window.HrcCampaign?.isSystemDiscountCode?.(code) === true;
    } catch (_) {
      return false;
    }
  }

  function visibleDiscountCodes(codes) {
    return (codes || []).filter((item) => !isCampaignSystemCode(item.code));
  }

  function getCouponApplyBlock(cart, code, context) {
    const active = getActiveCodes(cart);

    if (active.some((c) => codesEqual(c, code))) {
      return { blocked: false, alreadyApplied: true };
    }

    if (active.length > 0) {
      const visible = active.filter((c) => !isCampaignSystemCode(c));
      const onlyCampaign = visible.length === 0;
      return {
        blocked: true,
        message: onlyCampaign
          ? t("campaignCouponHidden", "Your special price is already active on this cart.")
          : t("discountAlreadyHasCoupon", 'The coupon "__CODE__" is already applied to your cart. Remove it before applying a new one.', { code: visible[0] }),
      };
    }

    if (context === "pdp") {
      const { variantId } = readPdpSelection();
      const couponed = getVariantCouponedItems(cart, variantId);
      if (couponed.length) {
        const existing = getCouponTitleFromItem(couponed[0]);
        return {
          blocked: true,
          message: existing
            ? t("discountProductHasCoupon", 'This product already has the "__CODE__" coupon. Remove it before applying another.', { code: existing })
            : t("discountAlreadyZero", "This product is already free with a coupon. Another coupon cannot be applied."),
        };
      }
    }

    return { blocked: false, alreadyApplied: false };
  }

  function isDiscountCodeApp(app) {
    if (!app) return false;
    const type = String(app.type || "").toLowerCase();
    if (!type) return Boolean(app.code);
    return type === "discount_code" || type === "discountcode";
  }

  function matchingCodeApp(cart, code) {
    const needle = normalizeCode(code);
    if (!needle || !cart) return null;
    const apps = [
      ...(cart.discount_applications || []),
      ...(cart.cart_level_discount_applications || []),
    ];
    return (
      apps.find(
        (app) => isDiscountCodeApp(app) && codesEqual(app.code || "", needle),
      ) || null
    );
  }

  function isDiscountActive(cart, code) {
    const needle = normalizeCode(code);
    if (!needle || !cart) return false;
    if (
      (cart.discount_codes || []).some(
        (d) => codesEqual(d.code, needle) && d.applicable === true,
      )
    ) {
      return true;
    }
    return Boolean(matchingCodeApp(cart, code));
  }

  function restoreDiscountCodes(codes) {
    return updateDiscount(Array.isArray(codes) ? codes.join(",") : String(codes || ""));
  }

  function cartHasVariant(cart, variantId) {
    const id = Number(variantId);
    if (!id) return false;
    return (cart?.items || []).some(
      (item) => Number(item.variant_id || item.id) === id,
    );
  }

  function readPdpSelection() {
    const variantInput = document.querySelector('[id^="hrc-variant-input-"]');
    const qtyInput = document.querySelector(".hrc-pdp__qty-input");
    const atcBtn = document.querySelector('[id^="hrc-atc-"]');
    const variantId = Number(variantInput?.value || 0);
    const quantity = Math.max(1, parseInt(qtyInput?.value || "1", 10) || 1);
    const available = !(atcBtn && atcBtn.disabled);
    return { variantId, quantity, available, zeroPrice: false };
  }

  function priceFromCartItem(item) {
    if (!item) return null;
    const qty = Math.max(1, Number(item.quantity) || 1);
    const original =
      Number(item.original_line_price) ||
      Number(item.original_price || 0) * qty ||
      Number(item.price || 0) * qty;
    const discountAmt =
      Number(item.total_discount) ||
      (item.discounts || []).reduce((sum, d) => sum + Number(d.amount || 0), 0);
    let final = Number(item.final_line_price);
    if (!Number.isFinite(final) || final < 0) {
      const discountedUnit = Number(
        item.discounted_price != null ? item.discounted_price : item.final_price,
      );
      if (Number.isFinite(discountedUnit) && discountedUnit >= 0) {
        final = discountedUnit * qty;
      } else {
        final = Number(item.line_price);
      }
    }
    if (!Number.isFinite(final) || final < 0) final = original;
    const save = Math.max(0, discountAmt || original - final);
    if (!(original > 0) || save <= 0) return null;
    return {
      original,
      final: Math.max(0, original - save),
      save,
      scope: "product",
    };
  }

  function readPdpOriginalCents() {
    const variantInput = document.querySelector('[id^="hrc-variant-input-"]');
    const qtyInput = document.querySelector(".hrc-pdp__qty-input");
    const unit = Number(
      variantInput?.getAttribute("data-price") ||
        document.querySelector("[data-hrc-clay-price][data-base-price]")?.getAttribute("data-base-price") ||
        0,
    );
    const quantity = Math.max(1, parseInt(qtyInput?.value || "1", 10) || 1);
    if (!(unit > 0)) return 0;
    return unit * quantity;
  }

  function summaryFromDiscountApps(cart, originalCents, code) {
    if (!cart || !(originalCents > 0) || !code) return null;
    const match = matchingCodeApp(cart, code);
    if (!match) return null;
    const value = parseFloat(match.value);
    if (!Number.isFinite(value) || value <= 0) return null;

    let save = 0;
    const type = String(match.value_type || "").toLowerCase();
    if (type.includes("percent")) {
      save = Math.round(originalCents * (value / 100));
    } else {
      const asCents = Math.round(value * 100);
      save = asCents <= originalCents ? asCents : Math.round(value);
    }
    save = Math.min(originalCents, Math.max(0, save));
    if (save <= 0) return null;
    return {
      original: originalCents,
      final: originalCents - save,
      save,
      scope: "product",
    };
  }

  function resolvePriceSummary(cart, variantId, code) {
    if (!isDiscountActive(cart, code)) return null;
    return (
      getPriceSummary(cart, variantId) ||
      summaryFromDiscountApps(cart, readPdpOriginalCents(), code) ||
      summaryFromDiscountApps(
        cart,
        Number(cart?.original_total_price || cart?.items_subtotal_price || 0),
        code,
      )
    );
  }

  function getPriceSummary(cart, variantId) {
    if (!cart) return null;
    const id = Number(variantId);

    if (id) {
      const items = (cart.items || []).filter(
        (item) => Number(item.variant_id || item.id) === id,
      );
      if (items.length) {
        const merged = items.reduce(
          (acc, item) => {
            const part = priceFromCartItem(item);
            if (!part) return acc;
            acc.original += part.original;
            acc.final += part.final;
            acc.save += part.save;
            return acc;
          },
          { original: 0, final: 0, save: 0 },
        );
        if (merged.save > 0) {
          return { ...merged, scope: "product" };
        }
      }
    }

    const original = Number(
      cart.original_total_price || cart.items_subtotal_price || 0,
    );
    const final = Number(cart.total_price || 0);
    const save = Number(cart.total_discount || Math.max(0, original - final));
    if (save <= 0) return null;
    return { original, final, save, scope: "cart" };
  }

  function notifyCartUpdated(cart) {
    window.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart } }));
    document.dispatchEvent(
      new CustomEvent("hrc:cart:updated", { detail: { cart } }),
    );
  }

  function renderApplied(rootEl, codes) {
    const list = rootEl.querySelector("[data-discount-applied]");
    if (!list) return;

    const visible = visibleDiscountCodes(codes);
    if (!visible.length) {
      list.innerHTML = "";
      list.hidden = true;
      return;
    }

    list.hidden = false;
    list.innerHTML = visible
      .map((item) => {
        const pending = !item.applicable;
        const chipClass = pending ? " is-pending" : "";
        const meta = item.applicable ? t("discountApplied", "Applied") : t("discountPending", "Pending");
        return `
          <li class="hrc-discount__chip${chipClass}" data-discount-code="${escapeHtml(item.code)}">
            <span class="hrc-discount__chip-code">${escapeHtml(item.code)}</span>
            <span class="hrc-discount__chip-meta">${meta}</span>
            <button
              type="button"
              class="hrc-discount__chip-remove"
              data-discount-remove="${escapeHtml(item.code)}"
              aria-label="${escapeHtml(t("discountRemoveAria", "Remove coupon __CODE__", { code: item.code }))}"
            >
              <svg viewBox="0 0 10 10" fill="none" aria-hidden="true">
                <path d="M1 1l8 8M9 1L1 9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>
              </svg>
            </button>
          </li>
        `;
      })
      .join("");
  }

  function setStatus(rootEl, message, type) {
    const status = rootEl.querySelector("[data-discount-status]");
    if (!status) return;
    if (!message) {
      status.hidden = true;
      status.textContent = "";
      status.classList.remove("is-success", "is-error");
      return;
    }
    status.hidden = false;
    status.textContent = message;
    status.classList.toggle("is-success", type === "success");
    status.classList.toggle("is-error", type === "error");
  }

  function fillModalPrices(modal, cart, variantId, summaryOverride) {
    const box = modal?.querySelector("[data-discount-modal-prices]");
    if (!box) return;

    const summary = summaryOverride || getPriceSummary(cart, variantId);
    const oldEl = box.querySelector("[data-discount-modal-old]");
    const newEl = box.querySelector("[data-discount-modal-new]");
    const saveEl = box.querySelector("[data-discount-modal-save]");

    if (!summary || !(summary.original > 0)) {
      box.hidden = true;
      box.classList.remove("is-discounted");
      if (oldEl) oldEl.textContent = "";
      if (newEl) newEl.textContent = "";
      if (saveEl) {
        saveEl.hidden = true;
        saveEl.textContent = "";
      }
      return;
    }

    box.hidden = false;
    box.classList.toggle("is-discounted", summary.save > 0);
    if (oldEl) oldEl.textContent = formatMoney(summary.original);
    if (newEl) {
      newEl.textContent = summary.save > 0 ? formatMoney(summary.final) : "";
    }
    if (saveEl) {
      if (summary.save > 0) {
        saveEl.hidden = false;
        saveEl.textContent =
          summary.scope === "product"
            ? t("discountSaveProduct", "Save {{ amount }}", { amount: formatMoney(summary.save) })
            : t("discountSaveCart", "Total {{ amount }} off", { amount: formatMoney(summary.save) });
      } else {
        saveEl.hidden = true;
        saveEl.textContent = "";
      }
    }
  }

  function setLoading(rootEl, loading) {
    const locked = rootEl.classList.contains("is-coupon-locked");
    const btn = rootEl.querySelector("[data-discount-submit]");
    const label = rootEl.querySelector("[data-discount-btn-label]");
    const spinner = rootEl.querySelector("[data-discount-btn-loading]");
    const input = rootEl.querySelector("[data-discount-input]");
    if (btn) btn.disabled = loading || locked;
    if (input) input.readOnly = loading || locked;
    if (label) label.hidden = loading;
    if (spinner) {
      spinner.hidden = !loading;
      spinner.setAttribute("aria-hidden", loading ? "false" : "true");
    }
  }

  function initDiscountRoot(rootEl) {
    if (rootEl.dataset.hrcDiscountReady === "1") return;
    rootEl.dataset.hrcDiscountReady = "1";

    const form = rootEl.querySelector("[data-discount-form]");
    const input = rootEl.querySelector("[data-discount-input]");
    const context = rootEl.dataset.context || "cart";
    const modal = rootEl.querySelector("[data-discount-modal]");
    let pendingCode = "";

    function syncFormLock(cart) {
      const codes = visibleDiscountCodes(getAppliedCodes(cart));
      const locked = codes.length > 0;
      const code = codes[0]?.code || "";
      const wasLocked = rootEl.classList.contains("is-coupon-locked");
      const submit = rootEl.querySelector("[data-discount-submit]");
      const clearBtn = rootEl.querySelector("[data-discount-clear]");

      if (input) {
        input.readOnly = locked;
        input.classList.toggle("is-locked", locked);
        if (locked) {
          input.value = code;
          input.classList.remove("is-invalid");
        } else if (wasLocked) {
          input.value = "";
        }
      }
      if (submit) submit.disabled = locked;
      if (clearBtn) {
        clearBtn.hidden = !locked;
        if (locked) {
          clearBtn.setAttribute("data-discount-remove", code);
          clearBtn.setAttribute(
            "aria-label",
            t("discountRemoveAria", "Remove coupon {{ code }}", { code }),
          );
        } else {
          clearBtn.removeAttribute("data-discount-remove");
        }
      }
      rootEl.classList.toggle("is-coupon-locked", locked);
    }

    function mountOverlay(el) {
      if (el && el.parentElement !== document.body) {
        document.body.appendChild(el);
      }
    }

    function unmountOverlay(el) {
      if (el && el.parentElement === document.body && rootEl.isConnected) {
        rootEl.appendChild(el);
      }
    }

    function syncBodyLock() {
      const pendingOpen = modal && !modal.hidden;
      document.body.classList.toggle("hrc-discount-modal-open", Boolean(pendingOpen));
    }

    async function previewCouponPrices(variantId, quantity, code, cartHint) {
      const previous = getActiveCodes(cartHint || {}).join(",");
      if (
        variantId &&
        cartHint &&
        cartHasVariant(cartHint, variantId) &&
        isDiscountActive(cartHint, code)
      ) {
        return {
          valid: true,
          cart: cartHint,
          summary: resolvePriceSummary(cartHint, variantId, code),
        };
      }

      if (!variantId) return { valid: false, cart: cartHint, summary: null };

      let peekKey = null;
      try {
        const added = await addVariantToCart(variantId, quantity);
        peekKey = added.key || added.items?.[0]?.key;
        let cart = await updateDiscount(code);
        if (!isDiscountActive(cart, code)) cart = await fetchCart();
        const valid = isDiscountActive(cart, code);
        const summary = valid ? resolvePriceSummary(cart, variantId, code) : null;
        if (peekKey) await changeLineQuantity(peekKey, 0);
        await restoreDiscountCodes(previous);
        return { valid, cart, summary };
      } catch (_) {
        if (peekKey) {
          try {
            await changeLineQuantity(peekKey, 0);
          } catch (_) {
            /* ignore */
          }
        }
        try {
          await restoreDiscountCodes(previous);
        } catch (_) {
          /* ignore */
        }
        return { valid: false, cart: cartHint, summary: null };
      }
    }

    async function openModal(code, cart, summaryOverride) {
      if (!modal) return;
      pendingCode = code;
      const { variantId } = readPdpSelection();
      const text = modal.querySelector("[data-discount-modal-text]");
      if (text) {
        text.hidden = false;
        text.textContent = t(
          "discountAppliedSuccess",
          "{{ code }} coupon applied.",
          { code },
        );
      }

      const originalCents = readPdpOriginalCents();
      const firstSummary =
        summaryOverride ||
        resolvePriceSummary(cart, variantId, code) ||
        (originalCents > 0 ? { original: originalCents, final: originalCents, save: 0, scope: "product" } : null);
      fillModalPrices(modal, cart, variantId, firstSummary);
      mountOverlay(modal);
      modal.hidden = false;
      modal.setAttribute("aria-hidden", "false");
      syncBodyLock();
      modal.querySelector("[data-discount-modal-atc]")?.focus();
    }

    function closeModal() {
      if (!modal) return;
      modal.hidden = true;
      modal.setAttribute("aria-hidden", "true");
      setModalAtcLoading(false);
      unmountOverlay(modal);
      syncBodyLock();
    }

    function setModalAtcLoading(loading) {
      if (!modal) return;
      const atcBtn = modal.querySelector("[data-discount-modal-atc]");
      const continueBtn = modal.querySelector("[data-discount-modal-continue]");
      const label = modal.querySelector("[data-discount-modal-atc-label]");
      const spinner = modal.querySelector("[data-discount-modal-atc-loading]");
      if (atcBtn) atcBtn.disabled = loading;
      if (continueBtn) continueBtn.disabled = loading;
      if (label) label.hidden = loading;
      if (spinner) {
        spinner.hidden = !loading;
        spinner.setAttribute("aria-hidden", loading ? "false" : "true");
      }
    }

    async function refreshAfterChange(cart) {
      renderApplied(rootEl, getAppliedCodes(cart));
      syncFormLock(cart);
    }

    async function applyCode(rawCode) {
      const code = normalizeCode(rawCode);
      if (!code) {
        setStatus(rootEl, t("discountEnterCode", "Please enter a coupon code."), "error");
        input?.classList.add("is-invalid");
        input?.focus();
        return;
      }

      input?.classList.remove("is-invalid");
      setLoading(rootEl, true);
      setStatus(rootEl, "");

      try {
        const cartBefore = await fetchCart();
        const gate = getCouponApplyBlock(cartBefore, code, context);

        if (gate.alreadyApplied) {
          syncFormLock(cartBefore);
          setStatus(rootEl, t("discountAlreadyApplied", "{{ code }} is already applied.", { code }), "success");
          if (context === "pdp") await openModal(code, cartBefore);
          return;
        }

        if (gate.blocked) {
          input?.classList.add("is-invalid");
          setStatus(rootEl, gate.message, "error");
          syncFormLock(cartBefore);
          return;
        }

        const previousCodes = getActiveCodes(cartBefore);

        let cart = await updateDiscount(code);
        if (!isDiscountActive(cart, code)) {
          cart = await fetchCart();
        }

        let peeked = null;
        if (!isDiscountActive(cart, code) && context === "pdp" && !(cart.items || []).length) {
          const { variantId, quantity, available } = readPdpSelection();
          if (variantId && available) {
            peeked = await previewCouponPrices(variantId, quantity, code, cartBefore);
          }
        }

        if (isDiscountActive(cart, code)) {
          await refreshAfterChange(cart);
          syncFormLock(cart);
          setStatus(rootEl, t("discountAppliedSuccess", "{{ code }} coupon applied.", { code }), "success");

          if (context === "pdp") {
            await openModal(code, cart);
            return;
          }

          window.setTimeout(() => window.location.reload(), 450);
          return;
        }

        await restoreDiscountCodes(previousCodes);
        const restored = await fetchCart();
        renderApplied(rootEl, getAppliedCodes(restored));
        syncFormLock(restored);

        if (peeked?.valid) {
          setStatus(rootEl, t("discountAppliedSuccess", "{{ code }} coupon applied.", { code }), "success");
          await openModal(code, peeked.cart, peeked.summary);
          return;
        }

        input?.classList.add("is-invalid");
        setStatus(
          rootEl,
          t("discountInvalid", "This coupon code is invalid. Please check the code."),
          "error",
        );
      } catch (err) {
        input?.classList.add("is-invalid");
        setStatus(
          rootEl,
          err?.message || t("discountApplyFailedRetry", "Coupon could not be applied. Please try again."),
          "error",
        );
      } finally {
        setLoading(rootEl, false);
      }
    }

    async function addProductAndApply() {
      const code = pendingCode;
      const { variantId, quantity, available } = readPdpSelection();

      if (!variantId) {
        setStatus(rootEl, t("selectVariant", "Please select a product variant."), "error");
        return;
      }
      if (!available) {
        setStatus(rootEl, t("notInStock", "This product is out of stock."), "error");
        return;
      }

      setModalAtcLoading(true);
      try {
        let cart = await fetchCart();

        // Another coupon may have become active meanwhile
        if (code) {
          const gate = getCouponApplyBlock(cart, code, "pdp");
          if (gate.blocked && !gate.alreadyApplied) {
            closeModal();
            setStatus(rootEl, gate.message, "error");
            syncFormLock(cart);
            return;
          }
        }

        if (!cartHasVariant(cart, variantId)) {
          await addVariantToCart(variantId, quantity);
          cart = await fetchCart();
        }

        if (code) {
          cart = await updateDiscount(code);
          if (!isDiscountActive(cart, code)) {
            cart = await fetchCart();
          }
          if (!isDiscountActive(cart, code)) {
            const previous = getActiveCodes(cart).filter((c) => !codesEqual(c, code));
            cart = await restoreDiscountCodes(previous);
            if (!isDiscountActive(cart, code)) cart = await fetchCart();
          }
        }

        await refreshAfterChange(cart);
        notifyCartUpdated(cart);
        closeModal();

        if (code && isDiscountActive(cart, code)) {
          setStatus(
            rootEl,
            t("discountAppliedWithProduct", "{{ code }} coupon applied. Product added to cart.", { code }),
            "success",
          );
        } else if (code) {
          pendingCode = "";
          setStatus(
            rootEl,
            t("discountProductAddedCouponFailed", "Product added to cart, but the coupon could not be applied to this cart."),
            "error",
          );
        } else {
          setStatus(rootEl, t("productAdded", "Product added to cart."), "success");
        }
      } catch (err) {
        setStatus(
          rootEl,
          err?.message || t("discountAddFailedRetry", "Could not add to cart. Please try again."),
          "error",
        );
        setModalAtcLoading(false);
      }
    }

    async function removeCode(codeToRemove) {
      if (isCampaignSystemCode(codeToRemove)) {
        setStatus(
          rootEl,
          t("campaignCouponHidden", "Your special price is already active on this cart."),
          "error",
        );
        return;
      }
      setLoading(rootEl, true);
      setStatus(rootEl, "");
      try {
        const cartBefore = await fetchCart();
        const remaining = getAppliedCodes(cartBefore)
          .map((c) => c.code)
          .filter((c) => !codesEqual(c, codeToRemove))
          .join(",");
        const cart = await updateDiscount(remaining);
        renderApplied(rootEl, getAppliedCodes(cart));
        syncFormLock(cart);
        setStatus(rootEl, t("discountRemoved", "Coupon removed."), "success");

        if (context === "cart") {
          window.setTimeout(() => window.location.reload(), 350);
        } else {
          notifyCartUpdated(cart);
        }
      } catch (err) {
        setStatus(
          rootEl,
          err?.message || t("discountRemoveFailed", "Could not remove coupon. Please try again."),
          "error",
        );
      } finally {
        setLoading(rootEl, false);
      }
    }

    form?.addEventListener("submit", (e) => {
      e.preventDefault();
      if (rootEl.classList.contains("is-coupon-locked")) return;
      applyCode(input?.value);
    });

    rootEl.addEventListener("click", (e) => {
      const btn = e.target.closest("[data-discount-remove], [data-discount-clear]");
      if (!btn || !rootEl.contains(btn)) return;
      e.preventDefault();
      const codeToRemove = btn.getAttribute("data-discount-remove") || input?.value;
      if (codeToRemove) removeCode(codeToRemove);
    });

    input?.addEventListener("input", () => {
      input.classList.remove("is-invalid");
      if (input.value) input.value = input.value.toUpperCase();
    });

    if (modal) {
      modal.querySelectorAll("[data-discount-modal-dismiss]").forEach((el) => {
        el.addEventListener("click", () => {
          closeModal();
          if (pendingCode) {
            setStatus(
              rootEl,
              t("discountSavedAnytime", '"__CODE__" saved. You can add it to your cart anytime.', { code: pendingCode }),
              "success",
            );
          }
        });
      });

      modal
        .querySelector("[data-discount-modal-continue]")
        ?.addEventListener("click", () => {
          closeModal();
          if (pendingCode) {
            setStatus(
              rootEl,
              t("discountSavedContinue", '"__CODE__" saved. You can continue shopping.', { code: pendingCode }),
              "success",
            );
          }
        });

      modal
        .querySelector("[data-discount-modal-atc]")
        ?.addEventListener("click", () => {
          addProductAndApply();
        });
    }

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && modal && !modal.hidden) closeModal();
    });

    // Lock form if a coupon is already active; price stays hidden until user applies
    fetchCart()
      .then((cart) => {
        syncFormLock(cart);
      })
      .catch(() => {});
  }

  function initAll() {
    document.querySelectorAll("[data-hrc-discount]").forEach(initDiscountRoot);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
